/**
 * DocsDrawer
 * 
 * Right-side resizable panel for in-app documentation.
 * IDE-style drawer that never navigates away from workspace.
 * 
 * Three states: closed, open (unpinned), open (pinned)
 * - Unpinned: Temporary reference, closes on Esc or click outside
 * - Pinned: Persistent tool window, stays open during work
 */
import React, { useEffect, useCallback, useRef, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faXmark,
    faChevronLeft,
    faChevronRight,
    faBook,
    faThumbtack,
} from '@fortawesome/free-solid-svg-icons';
import { useDocsStore } from '../../store/docsStore';
import { DocsSidebar } from './DocsSidebar';
import { DocsContent } from './DocsContent';
import { DocsSearch } from './DocsSearch';

interface DocsDrawerProps {
    /** When true, hides the drawer temporarily (focus mode) */
    hidden?: boolean;
}

export const DocsDrawer: React.FC<DocsDrawerProps> = ({ hidden = false }) => {
    const {
        isOpen,
        isPinned,
        width,
        closeDocs,
        togglePin,
        setWidth,
        activePage,
        history,
        historyIndex,
        goBack,
        goForward,
    } = useDocsStore();

    const drawerRef = useRef<HTMLDivElement>(null);
    const [isResizing, setIsResizing] = useState(false);

    // Handle resize drag
    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        e.preventDefault();
        setIsResizing(true);
    }, []);

    useEffect(() => {
        if (!isResizing) return;

        const handleMouseMove = (e: MouseEvent) => {
            const newWidth = window.innerWidth - e.clientX;
            setWidth(newWidth);
        };

        const handleMouseUp = () => {
            setIsResizing(false);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);

        return () => {
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };
    }, [isResizing, setWidth]);

    // Global keyboard shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Cmd/Ctrl + Shift + D: Open docs
            if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key === 'd') {
                e.preventDefault();
                useDocsStore.getState().openDocs();
                return;
            }

            // Cmd/Ctrl + \: Toggle pin
            if ((e.metaKey || e.ctrlKey) && e.key === '\\') {
                e.preventDefault();
                if (isOpen) {
                    togglePin();
                }
                return;
            }

            // Only handle these if docs are open
            if (!isOpen) return;

            // Escape to close (only if unpinned)
            if (e.key === 'Escape' && !isPinned) {
                closeDocs();
                return;
            }

            // Alt+Left/Right for history navigation
            if (e.altKey && e.key === 'ArrowLeft') {
                e.preventDefault();
                goBack();
            }
            if (e.altKey && e.key === 'ArrowRight') {
                e.preventDefault();
                goForward();
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isOpen, isPinned, closeDocs, togglePin, goBack, goForward]);

    // Click outside to close (only if unpinned)
    const handleBackdropClick = useCallback(() => {
        if (!isPinned) {
            closeDocs();
        }
    }, [isPinned, closeDocs]);

    // Don't render if closed or hidden (focus mode)
    if (!isOpen || hidden) return null;

    const canGoBack = historyIndex > 0;
    const canGoForward = historyIndex < history.length - 1;

    return (
        <>
            {/* Backdrop - only show for unpinned state */}
            {!isPinned && (
                <div
                    className="fixed inset-0 bg-black/20 z-40"
                    onClick={handleBackdropClick}
                />
            )}

            {/* Drawer */}
            <aside
                ref={drawerRef}
                className={`fixed right-0 top-0 h-full bg-white dark:bg-[#0f1923] z-50 flex flex-col shadow-2xl
                    ${isPinned ? 'border-l-2 border-cyan-600' : 'border-l border-gray-200 dark:border-white/[0.06]'}
                `}
                style={{ width }}
            >
                {/* Resize handle */}
                <div
                    className="absolute left-0 top-0 bottom-0 w-1 cursor-ew-resize hover:bg-cyan-500/50 transition-colors"
                    onMouseDown={handleMouseDown}
                />

                {/* Header */}
                <header className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-white/[0.06] bg-gray-50 dark:bg-[#0c1520]">
                    <div className="flex items-center gap-3">
                        <FontAwesomeIcon icon={faBook} className="text-cyan-500" />
                        <h2 className="font-semibold text-gray-900 dark:text-white">Documentation</h2>
                    </div>

                    <div className="flex items-center gap-1">
                        {/* History navigation */}
                        <button
                            onClick={goBack}
                            disabled={!canGoBack}
                            className={`p-2 rounded transition-colors ${canGoBack
                                ? 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                }`}
                            title="Back (Alt+Left)"
                        >
                            <FontAwesomeIcon icon={faChevronLeft} className="h-3 w-3" />
                        </button>
                        <button
                            onClick={goForward}
                            disabled={!canGoForward}
                            className={`p-2 rounded transition-colors ${canGoForward
                                ? 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                                : 'text-gray-300 dark:text-gray-600 cursor-not-allowed'
                                }`}
                            title="Forward (Alt+Right)"
                        >
                            <FontAwesomeIcon icon={faChevronRight} className="h-3 w-3" />
                        </button>

                        {/* Pin button */}
                        <button
                            onClick={togglePin}
                            className={`p-2 rounded transition-colors ml-2 ${isPinned
                                ? 'text-cyan-500 dark:text-cyan-400 bg-cyan-100 dark:bg-cyan-900/30 hover:bg-cyan-200 dark:hover:bg-cyan-900/50'
                                : 'text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10'
                                }`}
                            title={isPinned ? 'Unpin (Ctrl+\\)' : 'Pin (Ctrl+\\)'}
                        >
                            <FontAwesomeIcon
                                icon={faThumbtack}
                                className={`h-3.5 w-3.5 ${isPinned ? 'rotate-45' : ''}`}
                            />
                        </button>

                        {/* Close button */}
                        <button
                            onClick={closeDocs}
                            className="p-2 rounded text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                            title={isPinned ? 'Close' : 'Close (Esc)'}
                        >
                            <FontAwesomeIcon icon={faXmark} className="h-4 w-4" />
                        </button>
                    </div>
                </header>

                {/* Search */}
                <div className="px-4 py-3 border-b border-gray-200 dark:border-white/[0.06]">
                    <DocsSearch />
                </div>

                {/* Main content area */}
                <div className="flex-1 flex overflow-hidden">
                    {/* Sidebar navigation */}
                    <DocsSidebar />

                    {/* Content pane */}
                    <div className="flex-1 overflow-auto">
                        {activePage ? (
                            <DocsContent page={activePage} />
                        ) : (
                            <div className="p-6 text-center text-gray-400 dark:text-gray-500">
                                <FontAwesomeIcon icon={faBook} className="h-12 w-12 mb-4 opacity-30" />
                                <p>Select a topic from the sidebar</p>
                                <p className="text-sm mt-2">or search for what you need</p>
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer with keyboard hints */}
                <footer className="px-4 py-2 border-t border-gray-200 dark:border-white/[0.06] bg-gray-50/80 dark:bg-[#0c1520]/50 text-xs text-gray-500 flex items-center justify-between">
                    <span>
                        {isPinned ? 'Pinned' : 'Press Esc to close'}
                    </span>
                    <span className="flex items-center gap-3">
                        <kbd className="px-1.5 py-0.5 bg-gray-100 dark:bg-white/10 rounded text-gray-500 dark:text-gray-400">Ctrl+\</kbd>
                        <span>toggle pin</span>
                    </span>
                </footer>
            </aside>
        </>
    );
};

export default DocsDrawer;
